import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react'

/** A folga, em px, que ainda conta como "está no fim da conversa". */
const NEAR_BOTTOM_PX = 80

/**
 * A conversa abre na mensagem mais nova (06/10).
 *
 * Os vendedores da Elite abriam um atendimento e caíam no começo da conversa,
 * e tinham de rolar até o fim para ver o que o cliente acabou de mandar.
 *
 * - **Trocar de conversa** (`key`) volta para o fim.
 * - **Mensagem nova** (`content`) desce junto, mas só se a pessoa já estava no
 *   fim. Quem subiu para reler uma mensagem antiga não é arrancado de lá.
 * - **Foto que termina de carregar** depois da rolagem aumenta a altura. O
 *   `load` é ouvido na captura, porque não sobe da `<img>` para o quadro, e
 *   desce de novo enquanto a pessoa estiver no fim.
 */
export const useStickToBottom = <T extends HTMLElement>(
  key: string,
  content: unknown
): RefObject<T | null> => {
  const ref = useRef<T>(null)
  const atBottom = useRef(true)

  useLayoutEffect(() => {
    atBottom.current = true
  }, [key])

  useLayoutEffect(() => {
    const element = ref.current
    if (element !== null && atBottom.current) {
      element.scrollTop = element.scrollHeight
    }
  }, [key, content])

  useEffect(() => {
    const element = ref.current
    if (element === null) return
    const onScroll = (): void => {
      atBottom.current =
        element.scrollHeight - element.scrollTop - element.clientHeight <=
        NEAR_BOTTOM_PX
    }
    const onLoad = (): void => {
      if (atBottom.current) element.scrollTop = element.scrollHeight
    }
    element.addEventListener('scroll', onScroll, { passive: true })
    element.addEventListener('load', onLoad, true)
    return () => {
      element.removeEventListener('scroll', onScroll)
      element.removeEventListener('load', onLoad, true)
    }
  }, [])

  return ref
}
